"use client";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import { Icon } from "@ospweb/sdk-ui";
import { cn, dayjs } from "utils";
import NextImage from "../../../NextImage";
import { AgentTextClassMap } from "../../const";
import Translations from "@translate/components/CoPilot/CopilotAgent/SourceReport/SourceReportItem";
import { I18nUtil } from "@utils/i18n";

const I18n = I18nUtil.createScoped(Translations);

interface SourceReportItemProps {
  thinking: string;
  agent: {
    name: string;
    avatar: string;
    label: string;
    id: string;
  };
  created: number;
  type: string;
  /**
   * @description 是否启用折叠展示，启用时根据 collapseLineLimit 控制折叠行数
   * @default true
   */
  enableCollapse?: boolean;
  /**
   * @description 折叠状态下保留的最大行数，仅在 enableCollapse 为 true 时生效
   * @default 3
   */
  collapseLineLimit?: number;
  className?: string;
}

export const SourceReportItem: FC<SourceReportItemProps> = ({
  thinking,
  agent,
  created,
  type,
  enableCollapse = true,
  collapseLineLimit = 3,
  className,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isExpandable, setIsExpandable] = useState(false);
  const contentRef = useRef<HTMLDivElement | null>(null);

  const handleOpenChange = useCallback(() => {
    if (!enableCollapse) {
      return;
    }
    setIsOpen((prev) => !prev);
  }, [enableCollapse]);

  useEffect(() => {
    if (!enableCollapse) {
      setIsExpandable(false);
      return;
    }

    const node = contentRef.current;
    if (!node) {
      return;
    }

    const evaluateOverflow = () => {
      if (typeof window === "undefined") {
        return;
      }

      const currentNode = contentRef.current;

      if (!currentNode || isOpen) {
        return;
      }

      const previousLineClamp = currentNode.style.webkitLineClamp;
      const previousDisplay = currentNode.style.display;
      const previousBoxOrient = currentNode.style.webkitBoxOrient;
      const previousOverflow = currentNode.style.overflow;
      let hasOverflow = false;

      try {
        currentNode.style.webkitLineClamp = "unset";
        currentNode.style.display = "block";
        currentNode.style.webkitBoxOrient = "initial";
        currentNode.style.overflow = "visible";

        const computedStyle = window.getComputedStyle(currentNode);
        const lineHeightValue = computedStyle.lineHeight;
        const fontSizeValue = computedStyle.fontSize;

        let resolvedLineHeight = Number.parseFloat(lineHeightValue);

        if (!Number.isFinite(resolvedLineHeight) || resolvedLineHeight <= 0) {
          const fallbackFontSize = Number.parseFloat(fontSizeValue);
          resolvedLineHeight =
            Number.isFinite(fallbackFontSize) && fallbackFontSize > 0
              ? fallbackFontSize * 1.2
              : 0;
        }

        if (resolvedLineHeight > 0) {
          const totalLines = currentNode.scrollHeight / resolvedLineHeight;
          hasOverflow = totalLines - Math.max(1, collapseLineLimit) > 0.01;
        } else {
          hasOverflow = false;
        }
      } finally {
        if (previousLineClamp) {
          currentNode.style.webkitLineClamp = previousLineClamp;
        } else {
          currentNode.style.removeProperty("-webkit-line-clamp");
        }

        if (previousDisplay) {
          currentNode.style.display = previousDisplay;
        } else {
          currentNode.style.removeProperty("display");
        }

        if (previousBoxOrient) {
          currentNode.style.webkitBoxOrient = previousBoxOrient;
        } else {
          currentNode.style.removeProperty("-webkit-box-orient");
        }

        if (previousOverflow) {
          currentNode.style.overflow = previousOverflow;
        } else {
          currentNode.style.removeProperty("overflow");
        }
      }

      setIsExpandable((prev) => (prev === hasOverflow ? prev : hasOverflow));
    };

    evaluateOverflow();

    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(() => {
        evaluateOverflow();
      });
      observer.observe(node);

      return () => {
        observer.disconnect();
      };
    }

    if (typeof window !== "undefined") {
      const handleResize = () => {
        evaluateOverflow();
      };

      window.addEventListener("resize", handleResize);

      return () => {
        window.removeEventListener("resize", handleResize);
      };
    }

    return undefined;
  }, [collapseLineLimit, enableCollapse, isOpen, thinking, agent, type]);

  useEffect(() => {
    if ((!enableCollapse || !isExpandable) && isOpen) {
      setIsOpen(false);
    }
  }, [enableCollapse, isExpandable, isOpen]);

  const shouldClamp = enableCollapse && isExpandable && !isOpen;
  const normalizedLineLimit = Math.max(1, collapseLineLimit);

  const contentClassName = cn(
    "body_m text-content_primary",
    shouldClamp ? "overflow-hidden" : "",
  );

  const contentStyle = shouldClamp
    ? {
        display: "-webkit-box",
        WebkitLineClamp: normalizedLineLimit,
        WebkitBoxOrient: "vertical" as const,
      }
    : undefined;

  if (!thinking) {
    return null;
  }

  return (
    <div
      className={cn("flex gap-2", className)}
      data-tracking-scope="source-report-item"
    >
      <div className="w-8 h-8">
        <NextImage
          src={agent?.avatar || "/assets/images/crypto-report-icon.webp"}
          alt={type}
          width={32}
          height={32}
          className="rounded-full"
        />
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <span
            className={cn(
              "body_bold_s",
              AgentTextClassMap[
                agent?.label as keyof typeof AgentTextClassMap
              ] || AgentTextClassMap.default,
            )}
          >
            {I18n.t("Agent") + " "}
            {agent?.name || ""}
          </span>
          {created && (
            <span className="body_s text-content_tertiary">
              {dayjs(created).format("YYYY-MM-DD HH:mm")}
            </span>
          )}
        </div>
        <div className="flex flex-col gap-2 p-3 rounded-3 border border-border_solid mt-2 bg-background_solid_secondary">
          <div
            className={contentClassName}
            data-testid="source-report-item__content"
            ref={contentRef}
            style={contentStyle}
          >
            {thinking}
          </div>
          {enableCollapse && isExpandable ? (
            <button
              aria-label="Source Report Item"
              data-openreplay-label="Source Report Item Button"
              id="btn-source-report-item"
              aria-expanded={isOpen}
              className="button flex items-center gap-1 self-end text-content_tertiary hover:text-content_secondary"
              onClick={handleOpenChange}
              type="button"
            >
              <span className="button_s">
                {isOpen ? I18n.t("Close Details") : I18n.t("See more")}
              </span>
              <Icon
                name="chevron-down"
                size={16}
                className={cn(
                  "transition-transform duration-200",
                  isOpen ? "rotate-180" : "",
                )}
              />
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
};
